import { createCoreServiceClient } from "@/lib/coreServiceApiClient";

const propertyApi = createCoreServiceClient(
  "/us-supply-properties",
  "core-service-properties"
);

const portfolioPropertyApi = createCoreServiceClient(
  "/aticahub/properties",
  "core-service-portfolio-properties",
  true
);

export type Property = {
  propertyId: string;
  propertyName: string;
};

/** Deal feature toggles. Absent (not false) when the backend has no value. */
export type PropertyFeatureFlags = {
  isRevmanEnabled?: boolean | null;
  isProactiveEnabled?: boolean | null;
  isReactiveEnabled?: boolean | null;
};

export type PortfolioProperty = Property & PropertyFeatureFlags;

export type PortfolioPropertySummaryData = {
  portfolioPropertySummaries: PortfolioProperty[];
  nonPortfolioPropertySummaries: PortfolioProperty[];
};

interface PropertyInfo extends PropertyFeatureFlags {
  propertyId?: string;
  propertyName?: string;
}

const mapProperties = (items: PropertyInfo[]): Property[] =>
  Array.isArray(items)
    ? items
        .map((item) => ({
          propertyId: String(item.propertyId ?? ""),
          propertyName: item.propertyName ?? "",
        }))
        .filter((p): p is Property => !!p.propertyId && !!p.propertyName)
    : [];

export const ValidateData = async (): Promise<Property[]> => {
  try {
    const response = await propertyApi.get("/property-summary");

    const info = response?.data?.info;

    if (!Array.isArray(info)) {
      throw new Error(
        "Invalid info format: expected array but received " + typeof info
      );
    }

    return mapProperties(info);
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Property data validation failed: ${error.message}`);
    }
    throw new Error("Property data validation failed: Unknown error occurred");
  }
};

const mapPortfolioProperties = (items: PropertyInfo[]): PortfolioProperty[] =>
  Array.isArray(items)
    ? items
        .map((item) => ({
          propertyId: String(item.propertyId ?? ""),
          propertyName: item.propertyName ?? "",
          isRevmanEnabled: item.isRevmanEnabled,
          isProactiveEnabled: item.isProactiveEnabled,
          isReactiveEnabled: item.isReactiveEnabled,
        }))
        .filter((p) => !!p.propertyId && !!p.propertyName)
    : [];

export const getPortfolioPropertySummary =
  async (): Promise<PortfolioPropertySummaryData> => {
    const response = await portfolioPropertyApi.get(
      "/portfolio-property-summary"
    );
    const info = response?.data?.info;
    return {
      portfolioPropertySummaries: mapPortfolioProperties(
        info?.portfolioPropertySummaries ?? []
      ),
      nonPortfolioPropertySummaries: mapPortfolioProperties(
        info?.nonPortfolioPropertySummaries ?? []
      ),
    };
  };
